import { clsx } from 'clsx';
import {
  forwardRef,
  memo,
  useMemo,
  useRef,
  useSyncExternalStore,
  type PropsWithChildren,
} from 'react';

import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import type { AriaLabelingProps } from '../../core/types/a11y-props.js';
import type { BehaviorTrackingProps } from '../../core/types/behavior-tracking-props.js';
import type { DataTestId } from '../../core/types/data-props.js';
import type { MaskingProps } from '../../core/types/masking-props.js';
import type { StylingProps } from '../../core/types/styling-props.js';
import type { ExtractedContentSlot } from '../split-layout/slots.js';
import { DetailsRegion } from './details-region/DetailsRegion.js';
import * as styles from './PageLayout.sty.js';
import {
  PageLayoutPortalContext,
  usePageLayoutPortalContext,
} from './shared/slots/PageLayoutPortalContext.js';
import {
  PageLayoutContent,
  PageLayoutDetailsWithControlBar,
  PageLayoutHeader,
  PageLayoutSidebar,
} from './shared/slots/portal-slots.js';
import { contentDefaults } from './shared/slots/slot-defaults.js';
import { createSlotRegistry } from './shared/slots/slot-registry.js';
import { TeleportTarget, useStableHost } from './shared/teleport/teleport.js';
import { SidebarRegion } from './sidebar-region/SidebarRegion.js';

function MainShell() {
  const { containers, hosts, registry } = usePageLayoutPortalContext();
  const snapshot = useSyncExternalStore(
    registry.subscribe,
    registry.getSnapshot,
  );

  const showTopLeftRadius = snapshot.sidebar !== null;
  const showTopRightRadius =
    snapshot.details !== null && snapshot.details.state.mode !== 'collapsed';

  const contentClassName = clsx(
    styles.content,
    showTopLeftRadius && styles.contentTopLeftRadius,
    showTopRightRadius && styles.contentTopRightRadius,
  );

  const sidebarTarget = useMemo(
    () => <TeleportTarget host={hosts.sidebar} />,
    [hosts.sidebar],
  );
  const detailsTarget = useMemo(
    () => <TeleportTarget host={hosts.details} />,
    [hosts.details],
  );
  const contentTarget = useMemo(
    () => <TeleportTarget host={hosts.content} />,
    [hosts.content],
  );

  // The registry entry carries everything the layouts need except the portal
  // target that stands in for the slot's children.
  const sidebarSlot = useMemo(
    () => snapshot.sidebar && { ...snapshot.sidebar, children: sidebarTarget },
    [snapshot.sidebar, sidebarTarget],
  );
  const detailsSlot = useMemo(
    () => snapshot.details && { ...snapshot.details, children: detailsTarget },
    [snapshot.details, detailsTarget],
  );

  // The `<main>` pane inside the details region. Its `minWidth` is the reserve
  // the main content keeps, which is what caps the details panel in split mode.
  const mainPaneSlot = useMemo<ExtractedContentSlot>(() => {
    if (snapshot.content) {
      const { config, domProps, ref } = snapshot.content;
      return {
        children: contentTarget,
        config,
        domProps: {
          ...domProps,
          className: clsx(contentClassName, domProps.className),
        },
        ref,
      };
    }
    return {
      children: undefined,
      config: { minWidth: contentDefaults.minWidth },
      domProps: { className: contentClassName },
    };
  }, [snapshot.content, contentTarget, contentClassName]);

  // Everything to the right of the sidebar, i.e. the whole details region. The
  // sidebar bounds itself via its own maxWidth, so this needs no minimum.
  const sidebarSiblingSlot = useMemo<ExtractedContentSlot<HTMLDivElement>>(
    () => ({
      children: (
        <DetailsRegion
          details={detailsSlot}
          content={mainPaneSlot}
          containerRef={containers.details}
        />
      ),
      config: { minWidth: 0 },
      domProps: {},
    }),
    [detailsSlot, mainPaneSlot, containers.details],
  );

  return (
    <SidebarRegion
      sidebar={sidebarSlot}
      content={sidebarSiblingSlot}
      containerRef={containers.sidebar}
    />
  );
}

function HeaderShell() {
  const { hosts, registry } = usePageLayoutPortalContext();
  const snapshot = useSyncExternalStore(
    registry.subscribe,
    registry.getSnapshot,
  );
  const header = snapshot.header;

  if (header === null) {
    return null;
  }

  return (
    <div
      {...header.domProps}
      ref={header.ref}
      className={clsx(styles.headerContainer, header.domProps.className)}
    >
      <TeleportTarget host={hosts.header} />
    </div>
  );
}

/** @public */
export interface PageLayoutProps
  extends
    PropsWithChildren,
    BehaviorTrackingProps,
    AriaLabelingProps,
    StylingProps,
    MaskingProps,
    DataTestId {}

const LayoutRoot = memo(
  forwardRef<HTMLDivElement, PageLayoutProps>((props, forwardedRef) => {
    const { children, className: rootClassName, ...rootProps } = props;

    const [behavioralTrackingProps, restRootProps] =
      useBehavioralTrackingProps(rootProps);

    const headerHost = useStableHost();
    const sidebarHost = useStableHost();
    const contentHost = useStableHost();
    const detailsHost = useStableHost();

    const sidebarContainerRef = useRef<HTMLDivElement>(null);
    const detailsContainerRef = useRef<HTMLDivElement>(null);

    const registry = useMemo(() => createSlotRegistry(), []);

    const context = useMemo(
      () => ({
        hosts: {
          header: headerHost,
          sidebar: sidebarHost,
          content: contentHost,
          details: detailsHost,
        },
        containers: {
          sidebar: sidebarContainerRef,
          details: detailsContainerRef,
        },
        registry,
      }),
      // hosts, containers and registry are stable
      // oxlint-disable-next-line react-hooks/exhaustive-deps
      [],
    );

    return (
      <PageLayoutPortalContext.Provider value={context}>
        <div
          {...restRootProps}
          {...behavioralTrackingProps}
          ref={forwardedRef}
          className={clsx(styles.layoutRoot, rootClassName)}
        >
          <HeaderShell />
          <div className={styles.contentContainer}>
            <MainShell />
            {children}
          </div>
        </div>
      </PageLayoutPortalContext.Provider>
    );
  }),
);
LayoutRoot.displayName = 'PageLayout';

/**
 * Use the `PageLayout` to structure app pages with persistent header, sidebar, content, and details panel slots, including responsive breakpoint handling and resizable panels.
 * @public
 */
export const PageLayout = Object.assign(LayoutRoot, {
  /** Displays content at the very top of the page. */
  Header: PageLayoutHeader,
  /** Displays content in the collapsible, resizable sidebar panel on the left. */
  Sidebar: PageLayoutSidebar,
  /**
   * Displays the styled main content area (padding, background, border-radius).
   */
  Content: PageLayoutContent,
  /** Displays content in the collapsible, resizable details panel on the right. */
  Details: PageLayoutDetailsWithControlBar,
});

export { usePageLayoutPortalContext };
